import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Alert, AlertDescription } from '@/shared/components/ui/alert';
import { Check, Copy, Mail, Link as LinkIcon } from 'lucide-react';
import { useSendAssessment } from '../../hooks/useLongevityAssessments';
import type { AssessmentLinkResponse } from '../../api/longevityAssessmentApi';

type SendAssessmentModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

type Mode = 'email' | 'link';

const GREEN = 'bg-[#026857] hover:bg-[#025247]';

function assessmentUrl(token: string): string {
  // Public assessment route (Slice 2). Composed from the link token.
  return `${window.location.origin}/longevity-assessment/${token}`;
}

function cooldownMessage(error: unknown): string {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 429) {
    return 'This client was contacted recently — please wait before resending.';
  }
  return 'Something went wrong sending the assessment. Please try again.';
}

/**
 * Send (or resend) a longevity assessment link to a client (Feature 095).
 * Both actions mint the same tokenized link. "Send via email" (deliveryMode EMAIL)
 * triggers the invitation email and shows the confirmation; "Share link directly"
 * (deliveryMode LINK) reveals a copyable link and sends no email.
 */
export function SendAssessmentModal({ open, onOpenChange }: SendAssessmentModalProps) {
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [mode, setMode] = useState<Mode | null>(null);
  const [result, setResult] = useState<AssessmentLinkResponse | null>(null);
  const [copied, setCopied] = useState(false);
  const sendAssessment = useSendAssessment();

  const canSubmit = clientName.trim().length > 0 && clientEmail.trim().length > 0;

  const handleSend = (nextMode: Mode) => {
    if (!canSubmit) return;
    setMode(nextMode);
    sendAssessment.mutate(
      {
        clientName: clientName.trim(),
        clientEmail: clientEmail.trim(),
        deliveryMode: nextMode === 'email' ? 'EMAIL' : 'LINK',
        // Every new send is MVP 2. V1 assessments already out resend on their
        // own version from the section's Resend button.
        questionSetVersion: '2',
      },
      { onSuccess: (res) => setResult(res) },
    );
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(assessmentUrl(result.linkToken));
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Clipboard unavailable — the link stays visible for manual copy.
    }
  };

  const handleClose = (next: boolean) => {
    if (!next) {
      setClientName('');
      setClientEmail('');
      setMode(null);
      setResult(null);
      setCopied(false);
      sendAssessment.reset();
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Send Longevity Readiness Assessment</DialogTitle>
          <DialogDescription>
            Email your client a secure invitation, or copy the link to share it yourself. Nothing is
            shared beyond you and your client.
          </DialogDescription>
        </DialogHeader>

        {!result ? (
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="assessment-client-name">Client name</Label>
              <Input
                id="assessment-client-name"
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="Brooks Mitchell"
                maxLength={200}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="assessment-client-email">Client email</Label>
              <Input
                id="assessment-client-email"
                type="email"
                value={clientEmail}
                onChange={(e) => setClientEmail(e.target.value)}
                placeholder="brooks.mitchell@example.com"
                maxLength={320}
              />
            </div>

            {sendAssessment.isError && (
              <Alert variant="destructive">
                <AlertDescription>{cooldownMessage(sendAssessment.error)}</AlertDescription>
              </Alert>
            )}

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button
                type="button"
                className={`flex-1 gap-2 ${GREEN}`}
                disabled={!canSubmit || sendAssessment.isPending}
                onClick={() => handleSend('email')}
              >
                <Mail className="h-4 w-4" />
                Send link via email
              </Button>
              <Button
                type="button"
                variant="outline"
                className="flex-1 gap-2"
                disabled={!canSubmit || sendAssessment.isPending}
                onClick={() => handleSend('link')}
              >
                <LinkIcon className="h-4 w-4" />
                Share link directly
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {mode === 'email' ? (
              <Alert>
                <AlertDescription>
                  <span className="flex items-center gap-2 font-medium text-[#026857]">
                    <Check className="h-4 w-4" /> Email sent to {result.clientEmail}
                  </span>
                  <span className="mt-2 block text-sm text-gray-600">
                    {result.clientName} received a secure link to a short, 15-minute assessment. Their
                    answers stay private and are shared only with you.
                  </span>
                </AlertDescription>
              </Alert>
            ) : (
              <div className="space-y-2">
                <Label htmlFor="assessment-link">Share this link directly with {result.clientName}</Label>
                <div className="flex gap-2">
                  <Input id="assessment-link" readOnly value={assessmentUrl(result.linkToken)} />
                  <Button type="button" variant="outline" className="gap-2" onClick={handleCopy}>
                    {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
              </div>
            )}

            <div className="flex justify-end">
              <Button type="button" className={GREEN} onClick={() => handleClose(false)}>
                Back to dashboard
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
